// Driver Management — create, edit, toggle active status
import { useState, useCallback, useMemo } from "react";
import { UserCheck, Plus, Pencil, Trash2, AlertCircle } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useDrivers,
  useCreateDriver,
  useUpdateDriver,
  useDeleteDriver,
} from "@/hooks/admin/useDrivers";
import type { Driver, DriverVehicleType, DriverPayload } from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SENEGAL_REGIONS: readonly { value: string; label: string }[] = [
  { value: "dakar", label: "Dakar" },
  { value: "kaolack", label: "Kaolack" },
  { value: "saint-louis", label: "Saint-Louis" },
  { value: "fatick", label: "Fatick" },
  { value: "thies-mbour", label: "Thiès-Mbour" },
  { value: "diourbel-touba", label: "Diourbel-Touba" },
] as const;

const VEHICLE_LABELS: Record<DriverVehicleType, string> = {
  moto: "Moto",
  voiture: "Voiture",
  camionnette: "Camionnette",
  velo: "Vélo",
};

const REGION_DISPLAY: Record<string, string> = Object.fromEntries(
  SENEGAL_REGIONS.map((r) => [r.value, r.label]),
);

const EMPTY_FORM: DriverPayload = {
  full_name: "",
  phone: "",
  vehicle_type: "moto",
  region: "dakar",
  is_available: true,
};

// ---------------------------------------------------------------------------
// Driver form modal
// ---------------------------------------------------------------------------

interface DriverFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly driver: Driver | null;
  readonly onSave: (payload: DriverPayload) => Promise<void>;
  readonly isSaving: boolean;
}

function DriverFormModal({
  open,
  onClose,
  driver,
  onSave,
  isSaving,
}: DriverFormModalProps) {
  const [form, setForm] = useState<DriverPayload>(
    driver
      ? {
          full_name: driver.full_name,
          phone: driver.phone,
          vehicle_type: driver.vehicle_type,
          region: driver.region,
          is_available: driver.is_available,
        }
      : EMPTY_FORM,
  );
  const [formError, setFormError] = useState<string | null>(null);

  // Reset when modal opens with new driver
  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    setFormError(null);
    onClose();
  }, [onClose]);

  const updateField = useCallback(
    <K extends keyof DriverPayload>(key: K, value: DriverPayload[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!form.full_name.trim()) {
      setFormError("Le nom est requis.");
      return;
    }
    if (!form.phone.trim()) {
      setFormError("Le téléphone est requis.");
      return;
    }
    setFormError(null);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Une erreur est survenue.",
      );
    }
  }, [form, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={driver ? "Modifier le chauffeur" : "Nouveau chauffeur"}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : null}
            {driver ? "Enregistrer" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Nom complet
          </label>
          <input
            type="text"
            value={form.full_name}
            onChange={(e) => updateField("full_name", e.target.value)}
            placeholder="Prénom Nom"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Téléphone
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => updateField("phone", e.target.value)}
            placeholder="+221 7X XXX XX XX"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-stone-700">
              Véhicule
            </label>
            <select
              value={form.vehicle_type}
              onChange={(e) =>
                updateField("vehicle_type", e.target.value as DriverVehicleType)
              }
              className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {(
                Object.entries(VEHICLE_LABELS) as [DriverVehicleType, string][]
              ).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-stone-700">
              Région
            </label>
            <select
              value={form.region}
              onChange={(e) => updateField("region", e.target.value)}
              className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            >
              {SENEGAL_REGIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="driver-active"
            type="checkbox"
            checked={form.is_available}
            onChange={(e) => updateField("is_available", e.target.checked)}
            className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
          />
          <label
            htmlFor="driver-active"
            className="text-sm font-medium text-stone-700"
          >
            Chauffeur actif
          </label>
        </div>

        {formError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

interface DeleteConfirmProps {
  readonly open: boolean;
  readonly driver: Driver | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly isDeleting: boolean;
}

function DeleteConfirm({
  open,
  driver,
  onConfirm,
  onCancel,
  isDeleting,
}: DeleteConfirmProps) {
  return (
    <AdminModal
      open={open}
      onClose={onCancel}
      title="Supprimer le chauffeur"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onCancel} disabled={isDeleting}>
            Annuler
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isDeleting}
            className="gap-1.5 bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : null}
            Supprimer
          </Button>
        </>
      }
    >
      <p className="text-sm text-stone-700">
        Êtes-vous sûr de vouloir supprimer{" "}
        <span className="font-semibold">{driver?.full_name}</span> ? Cette
        action est irréversible.
      </p>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Drivers() {
  const { data, isLoading, isError, refetch } = useDrivers();
  const createDriver = useCreateDriver();
  const updateDriver = useUpdateDriver();
  const deleteDriver = useDeleteDriver();

  const [search, setSearch] = useState("");
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deletingDriver, setDeletingDriver] = useState<Driver | null>(null);

  const drivers = data?.drivers ?? [];

  const filteredDrivers = useMemo(() => {
    if (!search.trim()) return drivers;
    const q = search.toLowerCase();
    return drivers.filter(
      (d) =>
        d.full_name.toLowerCase().includes(q) ||
        d.phone.toLowerCase().includes(q),
    );
  }, [drivers, search]);

  const handleCreateSave = useCallback(
    async (payload: DriverPayload) => {
      await createDriver.mutateAsync(payload);
      setIsCreateOpen(false);
    },
    [createDriver],
  );

  const handleEditSave = useCallback(
    async (payload: DriverPayload) => {
      if (!editingDriver) return;
      await updateDriver.mutateAsync({ id: editingDriver.id, payload });
      setEditingDriver(null);
    },
    [editingDriver, updateDriver],
  );

  const handleToggleActive = useCallback(
    (driver: Driver) => {
      updateDriver.mutate({
        id: driver.id,
        payload: { is_available: !driver.is_available },
      });
    },
    [updateDriver],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingDriver) return;
    await deleteDriver.mutateAsync(deletingDriver.id);
    setDeletingDriver(null);
  }, [deletingDriver, deleteDriver]);

  const columns: readonly ColumnDef<Driver>[] = useMemo(
    () => [
      {
        id: "full_name",
        header: "Nom",
        cell: (d) => (
          <span className="font-medium text-stone-900">{d.full_name}</span>
        ),
      },
      {
        id: "phone",
        header: "Téléphone",
        cell: (d) => (
          <span className="text-sm text-stone-600">{d.phone}</span>
        ),
      },
      {
        id: "vehicle_type",
        header: "Véhicule",
        cell: (d) => (
          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700">
            {VEHICLE_LABELS[d.vehicle_type] ?? d.vehicle_type}
          </span>
        ),
      },
      {
        id: "region",
        header: "Région",
        cell: (d) => (
          <span className="text-sm text-stone-600">{REGION_DISPLAY[d.region] ?? d.region}</span>
        ),
      },
      {
        id: "is_available",
        header: "Statut",
        cell: (d) => (
          <button
            type="button"
            onClick={() => handleToggleActive(d)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
              d.is_available
                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                : "bg-stone-100 text-stone-500 hover:bg-stone-200"
            }`}
          >
            {d.is_available ? "Actif" : "Inactif"}
          </button>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (d) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditingDriver(d)}
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Modifier"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setDeletingDriver(d)}
              className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Supprimer"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [handleToggleActive],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <UserCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Chauffeurs
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${drivers.length} chauffeur(s) enregistré(s)`}
              </p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateOpen(true)}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Nouveau chauffeur
          </Button>
        </div>

        {/* Search */}
        <div className="w-full sm:max-w-xs">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher par nom ou téléphone…"
          />
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les chauffeurs.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && filteredDrivers.length === 0 ? (
              <EmptyState
                icon={<UserCheck className="h-6 w-6" />}
                title="Aucun chauffeur"
                description="Créez votre premier chauffeur pour commencer."
                action={{ label: "Nouveau chauffeur", onClick: () => setIsCreateOpen(true) }}
              />
            ) : (
              <AdminDataTable
                data={filteredDrivers}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      {/* Create modal */}
      <DriverFormModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        driver={null}
        onSave={handleCreateSave}
        isSaving={createDriver.isPending}
      />

      {/* Edit modal */}
      <DriverFormModal
        open={editingDriver !== null}
        onClose={() => setEditingDriver(null)}
        driver={editingDriver}
        onSave={handleEditSave}
        isSaving={updateDriver.isPending}
      />

      {/* Delete confirmation */}
      <DeleteConfirm
        open={deletingDriver !== null}
        driver={deletingDriver}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeletingDriver(null)}
        isDeleting={deleteDriver.isPending}
      />
    </AdminLayout>
  );
}
